import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { ui, terms, content, LANGUAGES } from "./translations";

const I18nContext = createContext(null);
const STORAGE_KEY = "bpc_lang";
const LABEL_FIELDS = ["category", "topic", "kind", "type", "label"];

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved && ui[saved] ? saved : "en";
  });

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!ui[code]) return;
    window.localStorage.setItem(STORAGE_KEY, code);
    setLangState(code);
  }, []);

  // UI string lookup with English fallback + {var} interpolation
  const t = useCallback(
    (key, vars) => {
      let str = (ui[lang] && ui[lang][key]) ?? ui.en[key] ?? key;
      if (vars) {
        Object.keys(vars).forEach((k) => {
          str = str.replace(new RegExp(`\\{${k}\\}`, "g"), vars[k]);
        });
      }
      return str;
    },
    [lang]
  );

  // Translate a recurring domain term keyed by its English value
  const term = useCallback(
    (value) => (terms[lang] && terms[lang][value]) || value,
    [lang]
  );

  // Localize a collection of items: apply id-based overrides + label-field terms
  const localize = useCallback(
    (collection, items) => {
      if (!Array.isArray(items)) return items;
      const dict = (content[lang] && content[lang][collection]) || {};
      return items.map((it) => {
        const merged = { ...it, ...(it.id ? dict[it.id] || {} : {}) };
        LABEL_FIELDS.forEach((f) => {
          if (merged[f] && terms[lang] && terms[lang][merged[f]]) {
            merged[f] = terms[lang][merged[f]];
          }
        });
        return merged;
      });
    },
    [lang]
  );

  const value = { lang, setLang, t, term, localize, languages: LANGUAGES };
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};
